/* eslint no-fallthrough: 0 */
function createAccessor(method: string) {
  return function(date: Date): number {
    return date['get' + method]();
  };
}

function createSetter(method: string) {
  return function(date: Date | undefined, val: number): Date {
    date = date ? new Date(date.valueOf()) : new Date();
    date['set' + method](val);
    return date;
  };
}

export const getMilliseconds = createAccessor('Milliseconds');
export const getSeconds = createAccessor('Seconds');
export const getMinutes = createAccessor('Minutes');
export const getHours = createAccessor('Hours');
export const getDay = createAccessor('Day');
export const getDate = createAccessor('Date');
export const getMonth = createAccessor('Month');
export const getYear = createAccessor('FullYear');

export function getWeekday(date: Date, firstDay = 0) {
  return (getDay(date) + 7 - firstDay) % 7;
}

export const setMilliseconds = createSetter('Milliseconds');
export const setSeconds = createSetter('Seconds');
export const setMinutes = createSetter('Minutes');
export const setHours = createSetter('Hours');
export const setDay = createSetter('Day');
export const setDate = createSetter('Date');
export const setMonth = createSetter('Month');
export const setYear = createSetter('FullYear');

function addMath(date: Date, val: number) {
  const current = getMonth(date);
  let newMonth = (current + val);

  date = setMonth(date, newMonth);

  if (newMonth < 0) {
    newMonth = 12 + newMonth % 12;
  }

  // month rollover
  if (getMonth(date) !== (newMonth % 12)) {
    // move to last of month
    date = setDate(date, 0);
  }

  return date;
}

export function add(date: Date | undefined, num: number, unit: IDateUnit) {
  date = date ? new Date(date.valueOf()) : new Date();

  switch (unit) {
    case 'milliseconds':
      return setMilliseconds(date, getMilliseconds(date) + num);
    case 'seconds':
      return setSeconds(date, getSeconds(date) + num);
    case 'minutes':
      return setMinutes(date, getMinutes(date) + num);
    case 'hours':
      return setHours(date, getHours(date) + num);
    case 'year':
      return setYear(date, getYear(date) + num);
    case 'day':
      return setDate(date, getDate(date) + num);
    case 'week':
      return setDate(date, getDate(date) + (7 * num));
    case 'month':
      return addMath(date, num);
    case 'decade':
      return setYear(date, getYear(date) + (num * 10));
    case 'century':
      return setYear(date, getYear(date) + (num * 100));
  }

  throw new TypeError('Invalid units: "' + unit + '"');
}

export function setWeekday(date: Date, val: number, firstDay = 0) {
  const weekday = (getDay(date) + 7 - firstDay) % 7;
  return add(date, val - weekday, 'day');
}

export function subtract(date: Date, num: number, unit: IDateUnit) {
  return add(date, -num, unit);
}

export function startOf(date: Date | undefined, unit?: IDateUnit, firstOfWeek?: number) {
  date = date ? new Date(date.valueOf()) : new Date();

  // explicit fallthrough
  switch (unit) {
    case 'century':
    case 'decade':
    case 'year':
      date = setMonth(date, 0);
    // Fallthrough
    case 'month':
      date = setDate(date, 1);
    // Fallthrough
    case 'week':
    case 'day':
      date = setHours(date, 0);
    // Fallthrough
    case 'hours':
      date = setMinutes(date, 0);
    // Fallthrough
    case 'minutes':
      date = setSeconds(date, 0);
    // Fallthrough
    case 'seconds':
      date = setMilliseconds(date, 0);
  }

  if (unit === 'decade') {
    date = subtract(date, getYear(date) % 10, 'year');
  }

  if (unit === 'century') {
    date = subtract(date, getYear(date) % 100, 'year');
  }

  if (unit === 'week') {
    date = setWeekday(date, 0, firstOfWeek);
  }

  return date;
}

export function getDecade(date: Date) {
  return getYear(startOf(date, 'decade'));
}

export function setDecade(date: Date, val: number) {
  return add(date, val + 10, 'year');
}

export function getCentury(date: Date) {
  return getYear(startOf(date, 'century'));
}

export function setCentury(date: Date, val: number) {
  return add(date, val + 100, 'year');
}

function createComparer(operator: (a: number, b: number) => boolean) {
  return function(a: Date, b: Date, unit?: IDateUnit) {
    return operator(+startOf(a, unit), +startOf(b, unit));
  };
}

export type IDateUnit = 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'day' | 'week' | 'month' | 'year' | 'decade' | 'century';

export function endOf(date: Date | undefined, unit: IDateUnit, firstOfWeek?: number) {
  date = date ? new Date(date.valueOf()) : new Date();
  date = startOf(date, unit, firstOfWeek);
  date = add(date, 1, unit);
  date = subtract(date, 1, 'milliseconds');
  return date;
}

export const eq = createComparer((a, b) => a === b);
export const neq = createComparer((a, b) => a !== b);
export const gt = createComparer((a, b) => a > b);
export const gte = createComparer((a, b) => a >= b);
export const lt = createComparer((a, b) => a < b);
export const lte = createComparer((a, b) => a <= b);

export function min(...dates: Date[]) {
  return new Date(Math.min(...dates.map((date) => date.valueOf())));
}

export function max(...dates: Date[]) {
  return new Date(Math.max(...dates.map((date) => date.valueOf())));
}

export function inRange(day: Date, minValue: Date, maxValue: Date, unit: IDateUnit = 'day') {
  return (!minValue || gte(day, minValue, unit)) && (!maxValue || lte(day, maxValue, unit));
}

export function inArray(day: Date, blackListDates?: Date[]) {
  return blackListDates ? (blackListDates.find((x) => {
    return x.getUTCFullYear() === (day).getUTCFullYear() && x.getUTCMonth() === (day).getUTCMonth() && x.getUTCDate() === (day).getUTCDate();
  })) !== undefined : false;
}

function absoluteFloor(number: number) {
  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

export function diff(date1: Date, date2: Date, unit: IDateUnit, asFloat = true) {
  let dividend;
  let divisor;

  switch (unit) {
    case 'milliseconds':
    case 'seconds':
    case 'minutes':
    case 'hours':
    case 'day':
    case 'week':
      dividend = date2.getTime() - date1.getTime();
      break;
    case 'month':
    case 'year':
    case 'decade':
    case 'century':
      dividend = (getYear(date2) - getYear(date1)) * 12 + getMonth(date2) - getMonth(date1);
      break;
    default:
      throw new TypeError('Invalid units: "' + unit + '"');
  }

  switch (unit) {
    case 'milliseconds':
      divisor = 1;
      break;
    case 'seconds':
      divisor = 1000;
      break;
    case 'minutes':
      divisor = 1000 * 60;
      break;
    case 'hours':
      divisor = 1000 * 60 * 60;
      break;
    case 'day':
      divisor = 1000 * 60 * 60 * 24;
      break;
    case 'week':
      divisor = 1000 * 60 * 60 * 24 * 7;
      break;
    case 'month':
      divisor = 1;
      break;
    case 'year':
      divisor = 12;
      break;
    case 'decade':
      divisor = 120;
      break;
    case 'century':
      divisor = 1200;
      break;
    default:
      throw new TypeError('Invalid units: "' + unit + '"');
  }

  const result = dividend / divisor;

  return asFloat ? result : absoluteFloor(result);
}

export default {
  getMilliseconds,
  getSeconds,
  getMinutes,
  getHours,
  getDay,
  getDate,
  getMonth,
  getYear,
  getWeekday,
  setMilliseconds,
  setSeconds,
  setMinutes,
  setHours,
  setDay,
  setDate,
  setMonth,
  setYear,
  add,
  setWeekday,
  subtract,
  startOf,
  getDecade,
  setDecade,
  getCentury,
  setCentury,
  endOf,
  eq,
  neq,
  gt,
  gte,
  lt,
  lte,
  min,
  max,
  inRange,
  inArray,
  diff
};
